import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';

import Button from '@components/Button';
import { PrimaryButton, ProButton } from '@components/Button/variants';
import IconDeepResearchDoubleSparkle from '@components/Images/DeepResearch/IconDeepResearchDoubleSparkle';
import IconDeepResearchSmallI from '@components/Images/DeepResearch/IconDeepResearchSmallI';
import IconDeepResearchText from '@components/Images/DeepResearch/IconDeepResearchText';
import IconClose from '@components/Images/Rebranding/IconClose';
import IconHamburger from '@components/Images/Rebranding/IconHamburger';
import IconTI from '@components/Images/Rebranding/IconTI';
import LogoTIProText from '@components/Images/Rebranding/LogoTIProText';
import LogoTIText from '@components/Images/Rebranding/LogoTIText';
import SearchIcon from '@components/Images/SearchIcon';
import { SubmenuNavRef } from '@components/Nav/SubmenuNav';
import { ONBOARDING_STEP_IDS } from '@components/UserOnboardingTooltip/constants';
import {
  registerOnboardingAnchor,
  registerSettingsNavRef,
} from '@components/UserOnboardingTooltip/utils';
import useEventTracking from '@hooks/useEventTracking';
import useUserOnboardingTooltip from '@hooks/useUserOnboardingTooltip';
import {
  deep_research_path,
  new_session_path,
  root_path,
  search_page_path,
  subscribe_path,
  subscribe_promotion_path,
  upgrade_subscription_path,
} from '@routes';
import { ClickTarget, TrackingLocation, click } from '@tracking';

import LoggedInAvatar from '../LoggedInAvatar';
import SideNav from '../SideNav';
import TopNav from '../TopNav';
import { NavContentProps } from '../interfaces';
import SubscribeDropdownContent from './SubscribeDropdownContent';

const NavContent = ({
  analytics,
  className,
  currentUser,
  isScrollStyle,
  overrideOfferCode,
  proNavSections,
  currentUserCode,
}: NavContentProps) => {
  const [isSideNavOpen, setIsSideNavOpen] = useState(false);
  const [isMenuClicked, setIsMenuClicked] = useState(false);
  const settingsNavRef = useRef<SubmenuNavRef>(null);
  const { Track, trackEvent } = useEventTracking({
    ...analytics,
    location: TrackingLocation.Nav,
  });

  const { isSettingsNavForcedOpen } = useUserOnboardingTooltip();

  useEffect(() => {
    if (settingsNavRef.current) {
      registerSettingsNavRef(settingsNavRef.current);
    }
    return () => {
      registerSettingsNavRef(null);
    };
  }, []);

  const toggleSideNav = () => {
    setIsSideNavOpen(!isSideNavOpen);
    if (!isMenuClicked) {
      setIsMenuClicked(true);
    }
    trackEvent(
      click({
        clickTarget: ClickTarget.SideNavToggle,
        clickText: !isSideNavOpen ? 'open' : 'close',
      })
    );
  };

  return (
    <Track>
      <header id="global-nav" className={clsx(className, 'relative h-full nav:h-max')}>
        <div
          className={clsx('flex h-full items-center justify-center md:h-64 nav:h-[53px]', {
            'flex nav:hidden': isScrollStyle,
          })}
        >
          <div className="flex w-28 flex-1 gap-24 nav:hidden">
            <Button
              as="button"
              className={clsx('border-none bg-transparent p-0 outline-none', {
                'text-white': isScrollStyle,
                'text-black': !isScrollStyle,
              })}
              onClick={toggleSideNav}
            >
              <span
                className={clsx('flex', {
                  'animate-spin-once': isMenuClicked && isSideNavOpen,
                  'animate-spin-once-reverse': isMenuClicked && !isSideNavOpen,
                })}
              >
                {isSideNavOpen ? (
                  <IconClose className="w-20" />
                ) : (
                  <IconHamburger className="w-28" />
                )}
              </span>
            </Button>
            {isScrollStyle && (
              <a
                href={deep_research_path()}
                onClickCapture={() =>
                  trackEvent(click({ clickTarget: ClickTarget.DeepResearchIcon }))
                }
              >
                <IconDeepResearchDoubleSparkle className="block w-24 text-pro-gold nav:hidden" />
              </a>
            )}
          </div>
          <a
            href={root_path()}
            className={clsx('flex max-w-full flex-col justify-center nav:block', {
              'w-[190px] nav:w-[529px]': !currentUser.isProSubscriber && !isScrollStyle,
              'w-[190px] nav:w-[586px]': currentUser.isProSubscriber && !isScrollStyle,
            })}
            onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Logo }))}
          >
            {isScrollStyle ? (
              <IconTI className="w-24 transform-gpu text-primary-magenta" />
            ) : currentUser.isProSubscriber ? (
              <>
                <LogoTIProText
                  className="transform-gpu text-black nav:w-full"
                  aria-hidden
                  currentUserCode={currentUserCode}
                />
                <span className="sr-only">The Information Pro</span>
              </>
            ) : (
              <>
                <LogoTIText
                  className="transform-gpu text-black nav:w-full"
                  aria-hidden
                  currentUserCode={currentUserCode}
                />
                <span className="sr-only">The Information</span>
              </>
            )}
          </a>
          <div className="relative flex flex-1 items-center justify-end gap-28">
            <a
              ref={(el) => registerOnboardingAnchor(ONBOARDING_STEP_IDS.DeepResearchAndSearch, el)}
              href={deep_research_path({ size: currentUser.id })}
              className="group"
              onClickCapture={() =>
                trackEvent(click({ clickTarget: ClickTarget.DeepResearchIcon }))
              }
              aria-label="Deep Research landing page"
            >
              <IconDeepResearchText className="hidden w-[135px] transition duration-200 group-hover:text-primary-magenta lg:block" />
              <IconDeepResearchSmallI className="hidden w-[30px] nav:block lg:hidden" />
            </a>
            <a
              href={search_page_path()}
              className={clsx(
                'leading-[0] transition-colors hover:text-primary-magenta focus:text-primary-magenta',
                {
                  'block text-white': isScrollStyle,
                  'hidden text-black nav:block': !isScrollStyle,
                }
              )}
              onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.SearchButton }))}
              aria-label="Site search landing page"
            >
              <SearchIcon className="w-20" />
            </a>
            {currentUser.isSubscriber && !currentUser.isProSubscriber && (
              <ProButton
                as="link"
                className="hidden nav:block"
                href={upgrade_subscription_path()}
                onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Upgrade }))}
              >
                Upgrade to Pro
              </ProButton>
            )}
            {currentUser.isLoggedIn && !currentUser.isSubscriber && (
              <PrimaryButton
                as="link"
                className="hidden nav:block"
                href={
                  overrideOfferCode
                    ? subscribe_promotion_path({ offer: overrideOfferCode })
                    : subscribe_path()
                }
                onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Subscribe }))}
              >
                Subscribe
              </PrimaryButton>
            )}
            {currentUser.isLoggedIn ? (
              <LoggedInAvatar
                ref={settingsNavRef}
                currentUser={currentUser}
                forceOpen={isSettingsNavForcedOpen}
              />
            ) : (
              <>
                <Button
                  as="link"
                  className={clsx(
                    'hidden font-suisse-intl-mono text-overline-md transition-colors hover:text-primary-magenta focus:text-primary-magenta nav:block',
                    {
                      'text-white': isScrollStyle,
                      'text-black': !isScrollStyle,
                    }
                  )}
                  href={new_session_path()}
                  onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.SignIn }))}
                >
                  Sign in
                </Button>
                <div className="group relative">
                  <PrimaryButton
                    as="link"
                    className="z-1 relative !w-auto nav:px-36 nav:py-12 nav:text-button-lg"
                    size="xsmall"
                    href={
                      overrideOfferCode
                        ? subscribe_promotion_path({ offer: overrideOfferCode })
                        : subscribe_path()
                    }
                    onClickCapture={() => trackEvent(click({ clickTarget: ClickTarget.Subscribe }))}
                  >
                    Subscribe
                  </PrimaryButton>
                  <SubscribeDropdownContent
                    analytics={analytics}
                    className="absolute right-0 top-full hidden w-[400px] bg-white p-24 shadow-pro nav:group-hover:block"
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <TopNav
          currentUser={currentUser}
          isScrollStyle={isScrollStyle}
          proNavSections={proNavSections}
        />
        <SideNav
          currentUser={currentUser}
          isSideNavOpen={isSideNavOpen}
          setIsSideNavOpen={setIsSideNavOpen}
          proNavSections={proNavSections}
        />
      </header>
    </Track>
  );
};

export default NavContent;
